import { Injectable } from '@angular/core';
import { ApiService } from '../../core/api.service';
import { SyncCryptService } from '../../core/crypt/sync-crypt.service';
import { Store, select } from '@ngrx/store';
import * as fromRoot from '../../reducers';
import * as FileListActions from '../../actions/file-list.actions';
import {
  ERROR_CODES,
  ErrCode,
  FileItemType,
  PathListApiOutput,
  PathAddApiOutput,
  PathListApiInput,
  EncFile,
} from '../../shared/models';
import { UrlService } from '../../core/url.service';
import { DirtyOutService } from '../../core/crypt/dirty-out.service';
import { LoggerService } from '../../core/logger.service';
import { FileItemService } from '../../file/file-item.service';
import { Router } from '@angular/router';
import { UserService } from '../../core/user.service';
import { FileItem } from 'src/app/shared/models/files.model';
import { take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

const CACHE_EXPIRY = 10 * 60 * 1000;

interface CacheEntry {
  data: any;
  lastAccessed: number;
}

export type TGetDataResult = { pathlist: FileItem[]; updateState: boolean };

@Injectable({
  providedIn: 'root',
})
export class FileListService {
  private is_starred: boolean;
  private cache = new Map<string, CacheEntry>();
  private readonly cacheClearTimerId: any;

  constructor(
    private api: ApiService,
    private crypt: SyncCryptService,
    private dirtyOut: DirtyOutService,
    private store: Store<fromRoot.State>,
    private url: UrlService,
    private log: LoggerService,
    private fileItemService: FileItemService,
    private router: Router,
    private userService: UserService,
  ) {
    this.cacheClearTimerId = setInterval(() => {
      this.clearExpiredCacheEntries();
    }, CACHE_EXPIRY);
  }

  public clearResources(): void {
    if (this.cacheClearTimerId) {
      clearInterval(this.cacheClearTimerId);
    }
  }

  public getCachedData(syncId: any, histId: any): any {
    const key = `${syncId}-${histId}`;
    const entry = this.cache.get(key);
    if (!entry) {
      return;
    }
    entry.lastAccessed = Date.now();
    return entry.data;
  }

  public setCacheData(syncId: any, histId: any, data: any): void {
    const key = `${syncId}-${histId}`;
    this.cache.set(key, { data, lastAccessed: Date.now() });
  }

  private clearExpiredCacheEntries(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.lastAccessed > CACHE_EXPIRY) {
        this.cache.delete(key);
      }
    }
  }

  public reload(): void {
    this.store.dispatch(new FileListActions.FileListReloadAction());
  }

  private currentLoadKey: string | null = null;

  public async getData(syncId: number, histId: number, showdeleted: number, isVault: boolean): Promise<TGetDataResult> {
    const loadKey = `${syncId}-${histId}-${showdeleted}-${isVault}`;
    this.currentLoadKey = loadKey;
    try {
      const pathlist = await this.load(syncId, histId, showdeleted, '0', [], isVault, loadKey);

      return { pathlist, updateState: this.currentLoadKey === loadKey };
    } catch (e) {
      if (this.currentLoadKey !== loadKey) {
        return { pathlist: [], updateState: false };
      }
      throw ErrCode.fromException(e);
    }
  }

  public async getPathList(): Promise<FileItem[]> {
    const pathlist = await this.store.pipe(select(fromRoot.fileSelector), take(1)).toPromise();
    return pathlist ?? [];
  }

  // legacy helper
  public getListSubscription(): any {
    return this.store.select(fromRoot.getFileListState);
  }

  // legacy helper
  public getCwdSubscription(): any {
    return this.store.select(fromRoot.getFileListCwd);
  }

  public sortList(predicate: string, reverse: boolean): void {
    const sortData = {
      direction: reverse ? 'desc' : 'asc',
      active: predicate,
    };
    this.store.dispatch(new FileListActions.FileListSortAction(sortData));
  }

  // legacy helper
  public dispatch(input: PathListApiInput): void {
    const dispatch = new FileListActions.FileListRefreshAction(input);
    this.store.dispatch(dispatch);
  }

  public reset(): void {
    this.store.dispatch(new FileListActions.FileListResetAction());
  }

  public async getDataNoState(syncId: number, offset: string, pathlist: FileItem[] = []): Promise<PathListApiOutput> {
    const d = await this.api.execute<PathListApiOutput>('pathlist', {
      showdeleted: 0,
      hist_id: 0,
      sync_id: syncId,
      offset_metaname_digest: offset,
    });
    if (d.cwd) {
      d.cwd = await this.fileItemService.format(d.cwd);
    }
    if (d.pathlist && d.pathlist.length) {
      for (let i = 0, len = d.pathlist.length; i < len; i++) {
        pathlist.push(await this.fileItemService.format(d.pathlist[i]));
      }
      return await this.getDataNoState(syncId, d.last_metaname_digest, pathlist);
    } else {
      d.pathlist = pathlist;
      return d;
    }
  }

  public async load(
    syncId: number,
    histId: number,
    showdeleted: number,
    offset: string,
    pathlist: FileItem[] = [],
    isVault: boolean,
    loadKey: string,
  ): Promise<FileItem[]> {
    if (this.currentLoadKey !== loadKey) {
      return pathlist;
    }
    const updateDataPayload: Partial<PathListApiOutput> = {};
    const pathListApiOutput = await this.api.execute<PathListApiOutput>('pathlist', {
      showdeleted: showdeleted,
      hist_id: histId,
      sync_id: syncId,
      offset_metaname_digest: offset,
    });

    updateDataPayload.total_favorite = pathListApiOutput.total_favorite;
    updateDataPayload.progress = pathListApiOutput.progress;

    this.url.setHostMulti(pathListApiOutput);
    this.is_starred = !!this.router.url.includes('/starred');

    if (offset === '0') {
      if (!pathListApiOutput.cwd) {
        throw new ErrCode(ERROR_CODES.CWD_NOT_FOUND);
      }

      updateDataPayload.cwd = await this.fileItemService.format(pathListApiOutput.cwd);

      if (pathListApiOutput.parents) {
        const parents: EncFile[] = [];
        for (let i = 0, len = pathListApiOutput.parents.length; i < len; i++) {
          let name: string;
          if (
            pathListApiOutput.parents[i].metaname == 'web' ||
            pathListApiOutput.parents[i].metaname == 'Web Uploads'
          ) {
            name = 'Vault';
          } else if (this.is_starred && pathListApiOutput.parents[i].loc_id == this.userService.get('syncus_sync_id')) {
            name = 'Starred';
          } else {
            try {
              name = await this.crypt.filenameDecrypt(pathListApiOutput.parents[i].metaname);
            } catch (ex) {
              name = pathListApiOutput.parents[i].metaname;
            }
          }
          parents.push(
            new EncFile({
              loc_id: pathListApiOutput.parents[i].loc_id,
              metaname: pathListApiOutput.parents[i].metaname,
              name: name,
            }),
          );
        }
        updateDataPayload.parents = parents;
      }
    }

    if (pathListApiOutput?.hist_id > 0) {
      updateDataPayload.hist_id = pathListApiOutput.hist_id;
    }

    this.store.dispatch(new FileListActions.FileListUpdateAction(updateDataPayload));

    if (pathListApiOutput.pathlist?.length) {
      for (let i = 0, len = pathListApiOutput.pathlist.length; i < len; i++) {
        if (
          pathlist.some((file: FileItem) => {
            return pathListApiOutput.pathlist[i].sync_id === file.sync_id;
          })
        ) {
          this.log.error(`Duplicate sync ids '${pathListApiOutput.pathlist[i].sync_id}' in pid '${syncId}'`);
          throw new ErrCode(ERROR_CODES.API_TALK_ERROR, 'Multiple files with the same Sync_id');
        }
        const file = pathListApiOutput.pathlist[i];
        file.is_vault = isVault;
        pathlist.push(await this.fileItemService.format(file));
      }

      // TODO remove recursive call after BE updates https://syncdev.atlassian.net/browse/UX-2406
      return await this.load(
        syncId,
        histId,
        showdeleted,
        pathListApiOutput.last_metaname_digest,
        pathlist,
        isVault,
        loadKey,
      );
    } else {
      return pathlist;
    }
  }

  public async mkdir(syncPid: number, name: string): Promise<PathAddApiOutput> {
    const keyArray = await this.dirtyOut.executeForSync(syncPid, name);
    const encName = await this.crypt.filenameEncrypt(name);
    return await this.api.execute<PathAddApiOutput>('pathadd', {
      sync_pid: syncPid,
      new_name: encName,
      share_names: keyArray,
    });
  }

  public filterList(searchValue: string): void {
    this.store.dispatch(new FileListActions.FilterListAction({ searchValue }));
  }

  public getItemLink = (item: FileItem): string => {
    let type = 'default';
    if (item.type === FileItemType.DIR && this.router.url.includes('files')) {
      return `/files/${item.sync_id}`;
    } else if (this.fileItemService.isImage(item)) {
      type = 'image';
    } else if (this.fileItemService.isText(item)) {
      type = 'text';
    } else if (
      this.fileItemService.isPDF(item) &&
      (item.context == 'link' || item.context == 'applink') &&
      item.previewonly
    ) {
      type = 'pdf';
    } else if (this.fileItemService.isPDF(item) && item.context == 'files') {
      type = 'pdf';
    } else if (this.displayOffice(item)) {
      type = 'office';
    } else if (this.displayDoc(item)) {
      type = 'doc';
    } else if (this.fileItemService.isVideo(item)) {
      type = 'video';
    } else if (this.fileItemService.isAudio(item)) {
      type = 'audio';
    } else {
      type = 'default';
    }
    return `/file/${item.sync_id}/view/${type}`;
  };

  public displayOffice(item: FileItem): boolean {
    return (
      this.fileItemService.isMSOffice(item, 'embedview') &&
      // Only enabled in files, for now
      item.context === 'files' &&
      this.userService.get('is_office') === 1
    );
  }
  public displayDoc(item: FileItem): boolean {
    return (
      this.fileItemService.isDocument(item) &&
      // only compat links
      ((item.compat && (item.context == 'link' || item.context == 'applink')) || item.context == 'files')
    );
  }

  public getAvatarUrl = (userId: number): string => {
    return `${environment.logohost}avatar/saved/${userId}`;
  };
  public onViewHistory = (syncId: number): void => {
    this.router.navigate([`files/file-history/${syncId}`]);
  };
}
